import { useState, useEffect } from 'react';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { useUserStore, useRiderStore, useAuthStore } from '../../stores';
import { useAddFavoriteDriver, useRemoveFavoriteDriver } from '../../hooks/rider-operations/useRiderMutations';

/**
 * Animated heart button for toggling a driver as favorite.
 * Shows empty heart when not favorited, filled pink heart when favorited.
 * Includes pop animation on toggle.
 */
function FavoriteButton({ driverId, size = 24, showLabel = false }) {
  const { isAuth } = useAuthStore();
  const { userId } = useUserStore();
  const { favoriteDrivers } = useRiderStore();

  // Check if this driver is currently in favorites
  const isDriverFavorited = favoriteDrivers.some((driver) => driver.id === driverId);

  // Local state for optimistic UI and animation
  const [isFavorite, setIsFavorite] = useState(isDriverFavorited);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync with store when favorites change
  useEffect(() => {
    setIsFavorite(isDriverFavorited);
  }, [isDriverFavorited]);

  // Get mutation hooks
  const { addFavoriteDriver } = useAddFavoriteDriver();
  const { removeFavoriteDriver } = useRemoveFavoriteDriver();

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Don't allow if not authenticated or already loading
    if (!isAuth || isLoading) return;

    // Start animation and loading
    setIsAnimating(true);
    setIsLoading(true);

    // Optimistic update
    const newFavoriteState = !isFavorite;
    setIsFavorite(newFavoriteState);

    // Reset animation after it completes
    setTimeout(() => setIsAnimating(false), 300);

    if (newFavoriteState) {
      // Add to favorites
      addFavoriteDriver({
        variables: {
          riderId: userId,
          driverId,
        },
        onCompleted: () => {
          setIsLoading(false);
        },
        onError: () => {
          // Revert on error
          setIsFavorite(false);
          setIsLoading(false);
        },
      });
    } else {
      // Remove from favorites
      removeFavoriteDriver({
        variables: {
          riderId: userId,
          driverId,
        },
        onCompleted: () => {
          setIsLoading(false);
        },
        onError: () => {
          // Revert on error
          setIsFavorite(true);
          setIsLoading(false);
        },
      });
    }
  };

  // If not authenticated, show disabled state with tooltip
  if (!isAuth) {
    return (
      <button
        className="p-2 rounded-full text-gray-400 cursor-not-allowed"
        disabled
        title="Log in to add favorites"
      >
        <FaRegHeart size={size} />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`
        ${showLabel ? 'flex items-center gap-2 px-4 py-2 rounded-lg' : 'p-2 rounded-full'}
        transition-all duration-200
        ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        ${isFavorite
          ? 'text-pink-500 hover:text-pink-600'
          : 'text-gray-400 hover:text-pink-400'
        }
        ${isAnimating ? 'favorite-pop' : ''}
        hover:bg-pink-50 active:scale-90
      `}
      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
    >
      {isFavorite ? (
        <FaHeart size={size} className={isAnimating ? 'favorite-heart-fill' : ''} />
      ) : (
        <FaRegHeart size={size} />
      )}
      {showLabel && (
        <span className="font-medium text-sm">
          {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        </span>
      )}
    </button>
  );
}

export default FavoriteButton;
